import React from 'react';
import {
  ClipboardCopyIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from 'lucide-react';
import { formatNumber } from './audioMatcherUtils';

export default function ResultSegment({
  segment,
  index,
  totalSegments,
  isExpanded,
  toggleJson,
  copyJson,
}) {
  return (
    <article className={`segment-card ${segment.isInfo ? 'segment-info' : ''}`}>
      <div className="seg-header">
        <div>
          <span className="seg-title">Segment {totalSegments - index}</span>
          <span className="seg-subtitle">{segment.status}</span>
        </div>
        <div className="seg-actions">
          <button
            type="button"
            onClick={() => toggleJson(segment.id)}
            className="icon-btn"
            aria-label="Toggle JSON"
          >
            {isExpanded ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
          </button>
          <button
            type="button"
            onClick={() => copyJson(segment.rawData)}
            className="icon-btn"
            aria-label="Copy JSON"
          >
            <ClipboardCopyIcon size={16} />
          </button>
        </div>
      </div>

      <div className="pill-row">
        {!segment.isInfo && <span className="pill">Accuracy {formatNumber(segment.accuracy, 3)}</span>}
        {segment.latency != null && <span className="pill">Latency {segment.latency} ms</span>}
        {segment.pitch != null && <span className="pill">Pitch {Math.round(segment.pitch)} Hz</span>}
        {segment.loudness != null && <span className="pill">Loudness {segment.loudness} dB</span>}
      </div>

      <div className="seg-body">
        {segment.isInfo ? (
          <div className="info-row">
            <p>{segment.rawData?.error || segment.rawData?.status || 'Info'}</p>
            {segment.rawData?.details && <p className="muted-copy">{segment.rawData.details}</p>}
          </div>
        ) : (
          <>
            {segment.transcript && (
              <div className="detail-row">
                <strong>Transcript</strong>
                <span className="mono">{segment.transcript}</span>
              </div>
            )}
            {segment.predicted && (
              <div className="detail-row">
                <strong>Predicted</strong>
                <span className="mono">{segment.predicted}</span>
              </div>
            )}
            {segment.expected && (
              <div className="detail-row">
                <strong>Expected</strong>
                <span className="mono">{segment.expected}</span>
              </div>
            )}
            {segment.actionWordLabel && (
              <div className="detail-row">
                <strong>Action Label</strong>
                <span>{segment.actionWordLabel}</span>
              </div>
            )}
            <div className="detail-grid">
              <div className="detail-box">
                <span className="detail-label">Chunk ID</span>
                <span className="mono">{segment.chunkId || '--'}</span>
              </div>
              <div className="detail-box">
                <span className="detail-label">Model</span>
                <span>{segment.modelId || '--'}</span>
              </div>
              <div className="detail-box">
                <span className="detail-label">Speech Rate</span>
                <span>{formatNumber(segment.speechRate)}</span>
              </div>
              <div className="detail-box">
                <span className="detail-label">Repetitions</span>
                <span>{segment.repetitions ?? '--'}</span>
              </div>
              <div className="detail-box">
                <span className="detail-label">Greedy Confidence</span>
                <span>{formatNumber(segment.greedyPathConfidence, 6)}</span>
              </div>
            </div>

            <div className="matches">
              <strong>Matches</strong>
              {segment.matches.length ? (
                <ul>
                  {segment.matches.map((match, matchIndex) => (
                    <li key={`${segment.id}-match-${matchIndex}`}>
                      {match.expected} ↔ {match.predicted} | {formatNumber(Number(match.match_score ?? 0))}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted-copy">No match details returned.</p>
              )}
            </div>

            {segment.perPhonemeConfidence.length > 0 && (
              <div className="confidence-row">
                {segment.perPhonemeConfidence.map((entry, entryIndex) => (
                  <span className="confidence-pill" key={`${segment.id}-conf-${entryIndex}`}>
                    {entry.phoneme}: {formatNumber(Number(entry.conf), 3)}
                  </span>
                ))}
              </div>
            )}
          </>
        )}

        {isExpanded && (
          <div className="raw-json-wrap">
            <pre className="raw-json">{JSON.stringify(segment.rawData, null, 2)}</pre>
          </div>
        )}
      </div>
    </article>
  );
}
